import { useCallback, useState } from 'react';

import queryString from 'query-string';
import { pipe, tap } from 'ramda';
import { useSelector } from 'react-redux';

import { BaseRadioButtonGroup } from 'components/shared/BaseFormComponents/RadioButton/RadioButtonGroup';
import Text from 'components/shared/Typography/Text';
import store from 'configureStore';
import { PAGE_STATUS_APPROVED, PAGE_TYPE_PROPERTY } from 'constants/index';
import { useEvent } from 'hooks/useEvent';
import { useSnackbar } from 'hooks/useSnackbar';
import { pageDataSelector } from 'store/page/selectors';
import {
  accountIdSelector,
  isAgentSelector,
  isOwnerSelector,
  userNameSelector,
  userSelector,
} from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import axiosApi from 'utils/axios';
import { copyTextToClipboard } from 'utils/common';
import { confirmAlert as confirmAlertWithChildren } from 'utils/contentPageAlert/contentPageAlert';
import { getSharingLinkByItinerariesItemType } from 'utils/itineraries/functions';

import s from './index.module.css';

const getSnackbarText = (pageType) => {
  switch (pageType) {
    case PAGE_TYPE_PROPERTY:
      return 'The Property Portal link has been copied to your clipboard.';

    default:
      return 'The link has been copied to your clipboard.';
  }
};

export const useHandleShare = ({ row, isSampleItinerary, isSampleLookbook }) => {
  const onSnackbar = useSnackbar();
  const isAgent = useSelector(isAgentSelector);
  const isOwner = useSelector(isOwnerSelector);
  const accountId = useSelector(accountIdSelector);

  const getTypeValue = () => {
    if (isAgent) return `&type=agent&acid=${accountId}`;
    if (isOwner) return `&type=owner&acid=${accountId}`;
    return '';
  };

  const getRoleSampleItineraryQuery = (systemWide = false) => {
    if (systemWide) return '';
    if (isAgent) return '&role=agent';
    return '';
  };

  const handleShare = () => {
    const pageType = row?.original?.type;
    const snackbarText = getSnackbarText(pageType);

    if (isSampleItinerary || isSampleLookbook) {
      const roleSampleItineraryQuery = getRoleSampleItineraryQuery(row?.original?.systemWide);
      const link = getSharingLinkByItinerariesItemType(isSampleItinerary ? 'sample' : 'sample_lookbook', {
        id: row?.original?.id,
        title: row?.original?.title,
      });
      copyTextToClipboard(`${link}${roleSampleItineraryQuery}`);
      onSnackbar(snackbarText);
      return;
    }

    const typeValue = getTypeValue();

    copyTextToClipboard(
      `${window.location.protocol}//travel.${window.location.host.replace('account.', '')}/${
        row?.original?.handle
      }?id=${row?.original?.id}${typeValue}`,
    );
    onSnackbar(snackbarText);
  };

  return { handleShare };
};

/**
 * Push Updates feature below
 */
const COMMON_TITLE = 'Your updates are now live.\n';
const AGENTS_TITLE = COMMON_TITLE + 'Notify others in your account?';
const NON_AGENTS_TITLE = COMMON_TITLE + 'Notify Safari Portal of these changes?';
const AGENTS_HINT_BOTTOM = 'Let your colleagues know about these updates.';
const NON_AGENTS_HINT_BOTTOM = 'Safari Portal curates a weekly digest of Supplier updates that\nis shared with Agents.';
const AGENTS_EMAIL_BODY =
  'Heads up! I’ve made the following changes to this page:\n' +
  '-\n' +
  '-\n' +
  '-\n' +
  'I’m letting you know in case you need to push any updates to proposals already ' +
  'using this page that you had made changes to.\n' +
  'Note that Safari Portal will automatically update this page in any proposals ' +
  'where the page was not manually updated, and you do not need to do anything ' +
  'in those cases. However, if you made any manual changes you should apply updates from the Content Library.';
const NON_AGENTS_EMAIL_BODY =
  'Safari Portal curates a weekly digest of Supplier updates that is shared with Agents.\n' +
  'Please describe your updates to this page below';

const getReactComponentWithBr = (text) => (
  <>
    {text.split('\n').map((p, i, arr) => (
      <>
        {p}
        {i < arr.length - 1 ? <br /> : ''}
      </>
    ))}
  </>
);

const getTitle = (isAgent) => getReactComponentWithBr(isAgent ? AGENTS_TITLE : NON_AGENTS_TITLE);
const getHintBottom = (isAgent) => getReactComponentWithBr(isAgent ? AGENTS_HINT_BOTTOM : NON_AGENTS_HINT_BOTTOM);

const getAlertModalStorageData = ({ accountId }) => {
  const hiddenAlertModalForAccounts = JSON.parse(localStorage.getItem('hideContentAlertModal')) ?? [];
  const currentHiddenAlertModal = hiddenAlertModalForAccounts.find((item) => item.accountId === accountId);
  const otherAccounts = hiddenAlertModalForAccounts.filter((item) => item.accountId !== accountId);

  return { currentHiddenAlertModal, otherAccounts };
};

export function usePushUpdatesNotify(values) {
  const isAgent = useSelector(isAgentSelector);
  const handleMailTo = useMailToHandler();
  const accountId = useSelector(accountIdSelector);

  const [radioButtonOption, setRadioButtonOption] = useState(null);

  const handleHiddenAlertModal = () => {
    const { currentHiddenAlertModal, otherAccounts } = getAlertModalStorageData({ accountId });

    const hiddenAlertModal = (() => {
      if (radioButtonOption === 'all') {
        return { forAll: true, forSome: [] };
      } else if (radioButtonOption === 'specific' && values?.id) {
        return { forSome: [...(currentHiddenAlertModal?.forSome ?? []), values.id], forAll: false };
      } else {
        return currentHiddenAlertModal;
      }
    })();

    const nextVal = [...otherAccounts, { ...currentHiddenAlertModal, accountId, ...hiddenAlertModal }];
    localStorage.setItem('hideContentAlertModal', JSON.stringify(nextVal));
  };

  const hundleSubmitAlert = useEvent((config) => {
    handleHiddenAlertModal();
    handleMailTo(config);
  });

  const handleCancelAlert = useEvent(() => {
    handleHiddenAlertModal();
  });

  const RadioButtonGroup = (props) => {
    const [value, setValue] = useState(props.value);

    return (
      <div className={s.radioButtonsWrapper}>
        <Text accent isStrong>
          Don’t show this modal
        </Text>
        <BaseRadioButtonGroup
          className={s.radioButtons}
          value={value}
          onChange={pipe(tap(props.onChange), setValue)}
          options={[
            { name: 'Again for this page', id: 'specific' },
            { name: 'Ever again', id: 'all' },
          ]}
        />
      </div>
    );
  };

  return useCallback(async () => {
    const config = {
      emails: ['updates@safariportal.app'],
      body: isAgent ? AGENTS_EMAIL_BODY : NON_AGENTS_EMAIL_BODY,
    };

    const { currentHiddenAlertModal } = getAlertModalStorageData({ accountId });

    const shouldShowPageAlert =
      !currentHiddenAlertModal?.forAll && !currentHiddenAlertModal?.forSome?.includes(values.id);

    if (isAgent && shouldShowPageAlert) {
      const membersEmails = await getMembersEmails();

      config.emails = membersEmails;

      confirmAlertWithChildren({
        title: getTitle(isAgent),
        hintBottom: getHintBottom(isAgent),
        children: <RadioButtonGroup value={radioButtonOption} onChange={setRadioButtonOption} />,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        onConfirm: () => hundleSubmitAlert(config),
        onCancel: () => handleCancelAlert(),
      });
    } else if (!isAgent) {
      confirmAlert({
        title: getTitle(isAgent),
        hintBottom: getHintBottom(isAgent),
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        onConfirm: () => handleMailTo(config),
      });
    }
  }, [handleMailTo, isAgent]);
}

async function getMembersEmails() {
  try {
    const state = store.getState();
    const accountId = accountIdSelector(state);
    const user = userSelector(state);

    const members = await axiosApi.get(`/acc/${accountId}/members`).then(({ data }) => data.data);

    return members.filter((m) => m.email !== user.email).map((m) => m.email);
  } catch (e) {
    console.error('getMembersEmails error', e);
    return [];
  }
}

function useMailToHandler() {
  const userName = useSelector(userNameSelector);
  const page = useSelector(pageDataSelector);

  return useCallback(
    async ({ emails, body }) => {
      const mailStruct = {
        subject: `${userName} has made changes to “${page.name}”`,
        body,
      };

      const qs = queryString.stringify(mailStruct);

      window.location.href = `mailto:${emails.join(',')}?${qs}`;
    },
    [page, userName],
  );
}

export function useHandleAfterNextDetails() {
  const pushUpdatesNotify = usePushUpdatesNotify();

  // important - do not use default parameters here, bc it is used by curry function
  return (savedPage, params) => {
    const { pageHadChangesBeforeSaving } = params;

    if (pageHadChangesBeforeSaving && savedPage.status === PAGE_STATUS_APPROVED) {
      pushUpdatesNotify();
    }
  };
}
